/**
 * FilingCard - SEC filing card with expandable summary
 */
import { useState } from 'react'
import type { SecFiling, FilingListItem } from '../../types/research'
import { SentimentBadge, toneToSentiment } from './SentimentBadge'

interface FilingCardProps {
  filing: SecFiling | FilingListItem
  defaultExpanded?: boolean
}

// Type guard to check if filing is a FilingListItem (has summary info)
function isFilingListItem(
  filing: SecFiling | FilingListItem
): filing is FilingListItem {
  return 'has_summary' in filing
}

// Filing type badge colors
const filingTypeBadge: Record<string, { bg: string; text: string }> = {
  '10-K': { bg: 'bg-blue-100', text: 'text-blue-800' },
  '10-Q': { bg: 'bg-purple-100', text: 'text-purple-800' },
  '8-K': { bg: 'bg-amber-100', text: 'text-amber-800' },
  DEF14A: { bg: 'bg-emerald-100', text: 'text-emerald-800' },
}

function getFilingBadgeColors(filingType: string) {
  return filingTypeBadge[filingType] ?? { bg: 'bg-gray-100', text: 'text-gray-800' }
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function getPeriodLabel(
  fiscalYear: number | null,
  fiscalQuarter: number | null,
  filingType: string
): string {
  if (!fiscalYear) return ''

  if (filingType === '10-K') {
    return `FY${fiscalYear}`
  }

  if (fiscalQuarter) {
    return `Q${fiscalQuarter} ${fiscalYear}`
  }

  return `${fiscalYear}`
}

export function FilingCard({ filing, defaultExpanded = false }: FilingCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const badgeColors = getFilingBadgeColors(filing.filing_type)
  const hasSummary = isFilingListItem(filing) && filing.has_summary
  const summaryPreview = isFilingListItem(filing) ? filing.summary_preview : null
  const managementTone = isFilingListItem(filing) ? filing.management_tone : null
  const periodLabel = getPeriodLabel(
    filing.fiscal_year,
    filing.fiscal_quarter,
    filing.filing_type
  )

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Filing type badge */}
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold ${badgeColors.bg} ${badgeColors.text}`}
            >
              {filing.filing_type}
            </span>

            {/* Period label */}
            {periodLabel && (
              <span className="text-sm font-medium text-gray-700">
                {periodLabel}
              </span>
            )}

            {/* Management tone sentiment */}
            {managementTone && (
              <SentimentBadge
                sentiment={toneToSentiment(
                  managementTone as 'optimistic' | 'cautious' | 'neutral'
                )}
                label={managementTone}
              />
            )}
          </div>

          {/* Date */}
          <span className="text-sm text-gray-500 whitespace-nowrap">
            {formatDate(filing.report_date)}
          </span>
        </div>

        {/* Summary toggle */}
        {hasSummary && summaryPreview && (
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-3 flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium focus:outline-none"
          >
            <svg
              className={`w-4 h-4 mr-1 transition-transform ${
                isExpanded ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
            {isExpanded ? 'Hide Summary' : 'Show Summary'}
          </button>
        )}
      </div>

      {/* Expandable summary section */}
      {isExpanded && summaryPreview && (
        <div className="px-4 pb-4 pt-0">
          <div className="bg-gray-50 rounded-md p-3">
            <p className="text-sm text-gray-700 leading-relaxed">
              {summaryPreview}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default FilingCard
